/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import _ from 'lodash';
import { withStateHandlers, compose } from 'recompose';

// Create State for type, instance Filter, and Instance Keys
// Set initial values and handlers for updating state
export default compose(
  withStateHandlers(
    {
      page: 'instances',
      instanceFilter: '',
      instanceKey: undefined,
      instanceOrProduct: null,
    },
    {
      nextPage: (
        { page, instanceKey, instanceOrProduct },
        { serviceSeverityGrid, severityGrid, requests, onClose }
      ) => {
        return (generalMessageEmailSubscribed, generalMessageSMSSubscribed) => {
          if (page === 'instances') {
            if (instanceOrProduct === 'instance') {
              if (instanceKey && instanceKey !== '_all') {
                const instance = requests.instanceList.value.find(i => {
                  return i.key === instanceKey;
                });
                const { emailVerified, mobileNumberVerified } = requests.subscriber.value;
                serviceSeverityGrid.actions.load({
                  email: instance.Services.map(service => {
                    return {
                      serviceKey: service.key,
                      major: emailVerified,
                      minor: emailVerified,
                      maintenance: emailVerified,
                    };
                  }),
                  sms: instance.Services.map(service => {
                    return {
                      serviceKey: service.key,
                      major: mobileNumberVerified,
                      minor: mobileNumberVerified,
                      maintenance: mobileNumberVerified,
                    };
                  }),
                });
                return { page: 'serviceSeverities' };
              }
            } else {
              // generalMessage
              return { page: 'generalMessage' };
            }
          } else if (page === 'serviceSeverities') {
            // Post new Subscription to Instance
            const allNotifications = _.every(serviceSeverityGrid.state.services, s => {
              return s.major && s.minor && s.maintenance;
            });
            const body = {
              instanceKey,
              type: 'INSTANCE',
              subType: 'email',
            };

            if (allNotifications) {
              body.allNotifications = true;
            } else {
              body.allNotifications = false;
              if (_.some(serviceSeverityGrid.state.services, s => s.major || s.minor || s.maintenance)) {
                body.ServiceSubscriptions = serviceSeverityGrid.state.services;
              } else {
                body.ServiceSubscriptions = [];
              }
            }

            requests.postSubscription
              .handleRequest({ body })
              .then(() => {
                const smsAllNotifications = _.every(serviceSeverityGrid.state.servicesSMS, s => {
                  return s.major && s.minor && s.maintenance;
                });
                const body = {
                  instanceKey,
                  type: 'INSTANCE',
                  subType: 'sms',
                };

                if (smsAllNotifications) {
                  body.allNotifications = true;
                } else {
                  body.allNotifications = false;
                  if (_.some(serviceSeverityGrid.state.servicesSMS, s => s.major || s.minor || s.maintenance)) {
                    body.ServiceSubscriptions = serviceSeverityGrid.state.servicesSMS;
                  } else {
                    body.ServiceSubscriptions = [];
                  }
                }
                requests.postSubscription.handleRequest({ body }).then(() => {
                  requests.subscriber.handleRequest();
                  onClose('success', 'Your subscription has been added to the list!');
                });
              })
              .catch(error => {
                requests.subscriber.handleRequest();
                onClose('failure', 'There was an error. Please try again');
              });
          } else if (page === 'allSeverities') {
            // Post new Subscription to All or Product
            let postPromise;
            let body = {};
            const statusObject = {
              major: severityGrid.state.major,
              minor: severityGrid.state.minor,
              maintenance: severityGrid.state.maintenance,
            };

            if (instanceOrProduct === 'product') {
              const product = {};
              product[instanceKey] = statusObject; 
              body = {
                emailAddress: requests.subscriber.value.emailAddress,
                subscriptions: {
                  products: product,
                },
              };
              postPromise = requests.postProductSubscription.handleRequest({ body });
            } else {
              body = Object.assign({ type: 'ALL' }, statusObject);
              postPromise = requests.postSubscription.handleRequest({ body });
            }

            postPromise
              .then(() => {
                requests.subscriber.handleRequest();
                onClose('success', 'Your subscription has been added to the list!');
              })
              .catch(() => {
                requests.subscriber.handleRequest();
                onClose('failure', 'There was an error. Please try again');
              });
          } else if (page === 'generalMessage') {
            const body = {
              generalMessageEmailSubscribed,
              generalMessageSMSSubscribed,
            };
            requests.patchSubscriber
              .handleRequest({ body })
              .then(() => {
                requests.subscriber.handleRequest();
                onClose('success', 'Your subscription has been added to the list!');
              })
              .catch(() => {
                requests.subscriber.handleRequest();
                onClose('failure', 'There was an error. Please try again');
              });
          }
        };
      },
      previousPage: ({ page }, { onClose }) => {
        return () => {
          if (page === 'instances') {
            onClose();
          } else if (page === 'serviceSeverities' || page === 'allSeverities' || page === 'generalMessage') {
            return { page: 'instances' };
          }
        };
      },
      setInstanceKey: () => {
        return value => {
          return {
            instanceKey: value,
          };
        };
      },
      setProductOrInstance: () => {
        return value => {
          return { instanceOrProduct: value };
        };
      },
    }
  )
);
